/**
 * TimelineEvent Compound Component
 *
 * Provides consistent layout structure for all timeline events.
 * Uses compound component pattern for flexibility.
 *
 * Usage:
 * <TimelineEvent ariaLabel="...">
 *   <TimelineEvent.Icon event={event} />  // or event_type_name="..." for items
 *   <TimelineEvent.Header timestamp="...">
 *     <InlineText>...</InlineText>
 *   </TimelineEvent.Header>
 *   <TimelineEvent.SubItems items={[...]} /> // optional
 * </TimelineEvent>
 */

import type { ReactNode } from 'react';

import { useViewStateLookups } from '../../context/TimelineLookupsContext';
import type { EventIconProps } from '../../types/renderer';
import type { SubItemContent } from '../../types/renderer';
import { AuditSummary } from './AuditSummary';
import { EventIcon } from './EventIcon';
import { EventTimestamp } from './EventTimestamp';
import { SubItemsList } from './SubItemsList';

// ============================================================================
// Main Container
// ============================================================================

interface TimelineEventProps {
  children: ReactNode;
  ariaLabel: string;
}

function TimelineEventRoot({ children, ariaLabel }: TimelineEventProps) {
  return (
    <article className='flex items-start gap-3 py-3' aria-label={ariaLabel}>
      {children}
    </article>
  );
}

// ============================================================================
// Icon Sub-component
// ============================================================================

function TimelineEventIcon(props: EventIconProps) {
  return <EventIcon {...props} />;
}

// ============================================================================
// Header Sub-component (text + timestamp on same line)
// ============================================================================

interface TimelineEventHeaderProps {
  children: ReactNode;
  timestamp: string;
}

function TimelineEventHeader({
  children,
  timestamp,
}: TimelineEventHeaderProps) {
  return (
    <div className='flex-1 flex items-center justify-between gap-4 min-w-0 min-h-7'>
      <div className='flex-1 min-w-0'>{children}</div>
      <EventTimestamp timestamp={timestamp} />
    </div>
  );
}

// ============================================================================
// Content Sub-component (header + optional sub-items or custom content in column)
// ============================================================================

interface TimelineEventContentProps {
  /** Header content (text/InlineText) */
  header: ReactNode;
  timestamp: string;
  /** Sub-items for bundled events */
  subItems?: SubItemContent[];
  /** Collapse sub-items beyond this count behind a "+N more" toggle. */
  subItemsCollapseAfter?: number;
  /** Label for the collapsed remainder; see SubItemsList.moreLabel. */
  subItemsMoreLabel?: (hiddenCount: number) => string;
  /** Custom content below header (e.g., a body preview) */
  children?: ReactNode;
  /** Event ID — when provided, automation audit summaries are rendered automatically */
  eventId?: string;
}

function TimelineEventContent({
  header,
  timestamp,
  subItems,
  subItemsCollapseAfter,
  subItemsMoreLabel,
  children,
  eventId,
}: TimelineEventContentProps) {
  const { getAuditsForEvent } = useViewStateLookups();
  const audits = eventId ? getAuditsForEvent(eventId) : undefined;

  return (
    <div className='flex-1 flex flex-col min-w-0'>
      <div className='flex items-center justify-between gap-4 min-h-7'>
        <div className='flex-1 min-w-0'>{header}</div>
        <EventTimestamp timestamp={timestamp} />
      </div>
      {subItems && subItems.length > 0 && (
        <SubItemsList
          items={subItems}
          collapseAfter={subItemsCollapseAfter}
          moreLabel={subItemsMoreLabel}
        />
      )}
      {children}
      {audits && audits.length > 0 && <AuditSummary audits={audits} />}
    </div>
  );
}

// ============================================================================
// SubItems Sub-component (standalone, for custom layouts)
// ============================================================================

interface TimelineEventSubItemsProps {
  items: SubItemContent[];
  /** Collapse sub-items beyond this count behind a "+N more" toggle. */
  collapseAfter?: number;
}

function TimelineEventSubItems({
  items,
  collapseAfter,
}: TimelineEventSubItemsProps) {
  return <SubItemsList items={items} collapseAfter={collapseAfter} />;
}

// ============================================================================
// Compound Component Export
// ============================================================================

export const TimelineEvent = Object.assign(TimelineEventRoot, {
  Icon: TimelineEventIcon,
  Header: TimelineEventHeader,
  Content: TimelineEventContent,
  SubItems: TimelineEventSubItems,
});
